import { apiClient, clean } from "./apiClient";
import type { PageRequest, PageResponse } from "@/types/api";
import type { DiaryEntryCreate } from "@/types/diaryCreate";
import type { DiaryEntryDetail, DiaryEntryFilter, DiaryEntrySummary } from "@/types/entities";

export interface DiaryAppointmentTypeOption {
  value: string;
  label: string;
}

function normalizeAppointmentTypeOption(raw: unknown): DiaryAppointmentTypeOption | null {
  if (typeof raw === "string" && raw.trim()) {
    const value = raw.trim().toUpperCase();
    const label = value.charAt(0) + value.slice(1).toLowerCase();
    return { value, label };
  }
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    const valueRaw = o.value ?? o.code ?? o.type ?? o.name;
    const labelRaw = o.label ?? o.displayName ?? o.name;
    if (typeof valueRaw === "string" && valueRaw.trim()) {
      const value = valueRaw.trim().toUpperCase();
      const label =
        typeof labelRaw === "string" && labelRaw.trim()
          ? labelRaw.trim()
          : value.charAt(0) + value.slice(1).toLowerCase();
      return { value, label };
    }
  }
  return null;
}

/** Backend page-size cap observed on GET /diary-entries (requests above this still return at most 200). */
const DIARY_LIST_PAGE_SIZE = 200;

const DIARY_LIST_MAX_PAGES = 50;

export const diaryService = {
  list(
    filter: DiaryEntryFilter,
    page: PageRequest,
    signal?: AbortSignal,
  ): Promise<PageResponse<DiaryEntrySummary>> {
    return apiClient
      .get<PageResponse<DiaryEntrySummary>>("/diary-entries", {
        params: { ...clean(filter), ...clean(page) },
        signal,
      })
      .then((r) => r.data);
  },
  /** Walk every page — needed when the legal-entity diary exceeds the backend page cap. */
  async listAll(
    filter: DiaryEntryFilter,
    pageBase: Omit<PageRequest, "page" | "size"> = {},
    maxPages = DIARY_LIST_MAX_PAGES,
    signal?: AbortSignal,
  ): Promise<DiaryEntrySummary[]> {
    const entries: DiaryEntrySummary[] = [];

    for (let page = 0; page < maxPages; page += 1) {
      signal?.throwIfAborted();
      // Load pages one at a time. Launching every remaining page concurrently
      // overloads the Diary API and leaves a large batch of cancelled requests
      // whenever the filters change before the batch completes.
      const response = await diaryService.list(filter, {
        ...pageBase,
        page,
        size: DIARY_LIST_PAGE_SIZE,
      }, signal);
      const content = Array.isArray(response.content) ? response.content : [];
      entries.push(...content);

      // Treat pagination metadata as untrusted. Only an explicit, internally
      // consistent `hasNext: true` is permission to issue another request.
      const totalPages = response.totalPages;
      const hasValidTotalPages = Number.isInteger(totalPages) && totalPages > page + 1;
      const isRequestedPage = Number.isInteger(response.page) && response.page === page;
      if (
        response.hasNext !== true ||
        content.length === 0 ||
        !hasValidTotalPages ||
        !isRequestedPage
      ) {
        return entries;
      }
    }

    console.warn(
      `[diaryService.listAll] Reached the ${maxPages}-page fetch cap; results may be incomplete.`,
    );
    return entries;
  },
  /** Full entry detail for drawer / edit — `GET /diary-entries/{id}` (id comes from list response). */
  getOne: (id: string): Promise<DiaryEntryDetail> =>
    apiClient.get<DiaryEntryDetail>(`/diary-entries/${id}`).then((r) => r.data),
  create: (body: DiaryEntryCreate): Promise<DiaryEntryDetail> =>
    apiClient.post("/diary-entries", body).then((r) => r.data),
  appointmentTypes: (): Promise<DiaryAppointmentTypeOption[]> =>
    apiClient.get<unknown>("/diary-entries/appointment-types").then((r) => {
      const data = r.data;
      if (!Array.isArray(data)) return [];
      return data
        .map((item) => normalizeAppointmentTypeOption(item))
        .filter((item): item is DiaryAppointmentTypeOption => Boolean(item));
    }),
  
  update: async (id: string, body: Record<string, unknown>) => {
    const current = await apiClient.get<Record<string, unknown>>(`/diary-entries/${id}`).then((r) => r.data);
    const merged = { ...current, ...body };
    return apiClient.patch(`/diary-entries/${id}`, merged).then((r) => r.data);
  },
  remove: (id: string) =>
    apiClient.delete(`/diary-entries/${id}`),
};
